import BreadCrump from "../tools/breadcrump";
import { getCookies } from "cookies-next";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { config } from "../../data/config";
import HeaderOfPage from "../tools/headerOfPage";
import { useTranslation } from "react-i18next";

const STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;

const STATUS_STYLE: Record<string, string> = {
    pending: "bg-amber-50 text-amber-800 ring-amber-200",
    approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    rejected: "bg-rose-50 text-rose-800 ring-rose-200",
    cancelled: "bg-zinc-100 text-zinc-600 ring-zinc-200",
};

const selectClass =
    "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";

function nextOpenFollowUp(item: any) {
    if (!Array.isArray(item?.followUps)) return null;
    return (
        item.followUps
            .filter((f: any) => !f.completed && f.scheduledAt)
            .sort(
                (a: any, b: any) =>
                    new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
            )[0] || null
    );
}

function displayName(item: any) {
    const user = item.user;
    if (user && (user.name || user.lastname)) {
        return `${user.name || ""} ${user.lastname || ""}`.trim();
    }
    return item.fullName || "—";
}

export default function ListMeterSales() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cookie = getCookies();
    const [list, setList] = useState<any[]>([]);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ property: "", status: "", method: "" });

    const breadcrump = [
        { name: t("Dashboard"), url: "/dashboard" },
        { name: t("Real Estate System"), url: "/dashboard/RealEstate" },
        { name: t("Meter sale requests"), url: null },
    ];

    const loadProperties = async () => {
        try {
            const res = await axios.get(`${config.url}/v1/dashboard/realestate/properties`, {
                headers: { Authorization: "Bearer " + cookie["token"] },
            });
            setProperties(Array.isArray(res.data.message) ? res.data.message : []);
        } catch (_) {
            setProperties([]);
        }
    };

    const load = async (next = filters) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (next.property) params.set("property", next.property);
            if (next.status) params.set("status", next.status);
            if (next.method) params.set("method", next.method);
            const qs = params.toString();
            const res = await axios.get(
                `${config.url}/v1/dashboard/realestate/meter-sales${qs ? `?${qs}` : ""}`,
                { headers: { Authorization: "Bearer " + cookie["token"] } }
            );
            setList(Array.isArray(res.data.message) ? res.data.message : []);
        } catch (_) {
            Swal.fire(t("Error"), t("Could not load requests."), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProperties();
        load();
    }, []);

    useEffect(() => {
        load(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.property, filters.status, filters.method]);

    const setStatus = async (e: React.MouseEvent, item: any, status: string) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await axios.put(
                `${config.url}/v1/dashboard/realestate/meter-sales/${item._id}`,
                { status },
                { headers: { Authorization: "Bearer " + cookie["token"] } }
            );
            load();
        } catch (err: any) {
            Swal.fire(
                t("Error"),
                err?.response?.data?.message || t("Could not update request."),
                "error"
            );
        }
    };

    const stats = useMemo(() => {
        const pending = list.filter((i) => i.status === "pending").length;
        const overdue = list.filter((i) => {
            const next = nextOpenFollowUp(i);
            return next && new Date(next.scheduledAt).getTime() < Date.now();
        }).length;
        return { total: list.length, pending, overdue };
    }, [list]);

    return (
        <div className="text-left bg-white dark:bg-zinc-900 p-3 m-4 directions min-h-[100vh] mb-5">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={t("Meter sale requests")} />

            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/60">
                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Total")}</p>
                    <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{stats.total}</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/30">
                    <p className="text-xs font-medium uppercase tracking-wide text-amber-700">{t("Pending")}</p>
                    <p className="mt-1 text-2xl font-semibold text-amber-900 dark:text-amber-100">{stats.pending}</p>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-900/40 dark:bg-rose-950/30">
                    <p className="text-xs font-medium uppercase tracking-wide text-rose-700">{t("Overdue follow-ups")}</p>
                    <p className="mt-1 text-2xl font-semibold text-rose-900 dark:text-rose-100">{stats.overdue}</p>
                </div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3 md:grid-cols-3 dark:border-zinc-700 dark:bg-zinc-800/40">
                <select
                    className={selectClass}
                    value={filters.property}
                    onChange={(e) => setFilters((prev) => ({ ...prev, property: e.target.value }))}
                    aria-label={t("All projects")}
                >
                    <option value="">{t("All projects")}</option>
                    {properties.map((p) => (
                        <option key={p._id} value={p._id}>
                            {p.code}
                        </option>
                    ))}
                </select>
                <select
                    className={selectClass}
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                    aria-label={t("All statuses")}
                >
                    <option value="">{t("All statuses")}</option>
                    {STATUSES.map((status) => (
                        <option key={status} value={status}>
                            {t(status)}
                        </option>
                    ))}
                </select>
                <select
                    className={selectClass}
                    value={filters.method}
                    onChange={(e) => setFilters((prev) => ({ ...prev, method: e.target.value }))}
                    aria-label={t("All methods")}
                >
                    <option value="">{t("All methods")}</option>
                    <option value="investment">{t("Investment")}</option>
                    <option value="residential_session">{t("Residential session")}</option>
                </select>
            </div>

            {loading ? (
                <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-16 text-center text-sm text-zinc-500 dark:border-zinc-700">
                    {t("Loading...")}
                </div>
            ) : list.length === 0 ? (
                <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-16 text-center dark:border-zinc-700">
                    <p className="text-base font-medium text-zinc-800 dark:text-zinc-100">{t("No requests yet.")}</p>
                    <p className="mt-1 text-sm text-zinc-500">{t("New meter requests from the website will show here.")}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((item) => {
                        const next = nextOpenFollowUp(item);
                        const overdue = next && new Date(next.scheduledAt).getTime() < Date.now();
                        const contact = [item.mobile, item.whatsapp, item.email].filter(Boolean);
                        return (
                            <div
                                key={item._id}
                                role="link"
                                tabIndex={0}
                                onClick={() => navigate(`/dashboard/RealEstate/meter-sales/${item._id}`)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        navigate(`/dashboard/RealEstate/meter-sales/${item._id}`);
                                    }
                                }}
                                className="group w-full cursor-pointer rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-blue-700"
                            >
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-base font-semibold text-zinc-900 dark:text-zinc-50">
                                                {item.property?.code || "—"}
                                            </span>
                                            <span
                                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLE[item.status] || STATUS_STYLE.pending}`}
                                            >
                                                {t(item.status)}
                                            </span>
                                            <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                                                {item.meters} m²
                                            </span>
                                            <span className="text-xs text-zinc-500">
                                                {item.method === "residential_session"
                                                    ? t("Residential session")
                                                    : t("Investment")}
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                                            {displayName(item)}
                                            {item.country ? (
                                                <span className="font-normal text-zinc-500"> · {item.country}</span>
                                            ) : null}
                                        </p>
                                        <p className="mt-1 truncate text-sm text-zinc-500">
                                            {contact.length ? contact.join(" · ") : t("No contact details")}
                                        </p>
                                        <p className="mt-2 text-xs text-zinc-400">
                                            {t("Created")}:{" "}
                                            {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}
                                        </p>
                                    </div>

                                    <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                                        <div
                                            className={`rounded-lg px-3 py-2 text-xs ${
                                                overdue
                                                    ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200"
                                                    : next
                                                      ? "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200"
                                                      : "bg-zinc-50 text-zinc-500 ring-1 ring-zinc-200"
                                            }`}
                                        >
                                            <span className="font-medium">{t("Next follow-up")}</span>
                                            <div className="mt-0.5">
                                                {next
                                                    ? `${new Date(next.scheduledAt).toLocaleString()}${overdue ? ` · ${t("Overdue")}` : ""}`
                                                    : t("None scheduled")}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Link
                                                to={`/dashboard/RealEstate/meter-sales/${item._id}`}
                                                className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                                            >
                                                {t("Open")}
                                            </Link>
                                            {item.status === "pending" && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                                                        onClick={(e) => setStatus(e, item, "approved")}
                                                    >
                                                        {t("Approve")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                                                        onClick={(e) => setStatus(e, item, "rejected")}
                                                    >
                                                        {t("Reject")}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
